/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@/shared/ui';
import type { PlanSegment } from '@/features/pricing';
import type { TrialStatus } from '../../hooks/use-trial-eligibility';

/** The text key and test id for each status that has something to say. */
const NOTES: Partial<Record<TrialStatus, (segment: PlanSegment) => { key: string; testId: string }>> = {
  eligible: (segment) => ({
    key: `planChoice.trialNote.${segment === 'solo' ? 'solo' : 'agency'}`, testId: 'trial-cap-note',
  }),
  ineligible: () => ({ key: 'planChoice.trialIneligible', testId: 'trial-ineligible-note' }),
};

/**
 * What the free period is worth to this buyer, in one line.
 *
 * Eligible: the cap that holds during the trial (RN-06) -- the screen must not
 * let the plan's full quota read as available from day one. Not eligible: the
 * first-subscription rule, once, without accusing (RN-12). Still loading or
 * failed: nothing, because the screen cannot say either without knowing
 * (AR-07).
 *
 * @param props - The eligibility and the segment buying.
 * @returns The note, or null.
 */
export const TrialEligibilityNotice: React.FC<{
  status: TrialStatus; segment: PlanSegment;
}> = ({ status, segment }) => {
  const { t } = useTranslation('@pages/onboarding');
  const note = NOTES[status]?.(segment);
  if (!note) return null;
  return (
    <Text
      component="p"
      data-testid={note.testId}
      style={{
        margin: 0, fontSize: 'var(--text-tag)', lineHeight: 1.45,
        color: 'var(--text-secondary)', textWrap: 'pretty',
      }}
    >
      {t(note.key)}
    </Text>
  );
};
